import React, { useState, useEffect, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import { connect } from "react-redux";
import DailyTargets from "../../library/Goals/DailyTargets";
import DailySectionTitle from "../../library/DailySectionTitle/DailySectionTitle";
import HorizontalProgressBarChart from "../../ui-library/HorizontalProgressBarChart/HorizontalProgressBarChart";
import EntriesCard from "../../library/EntriesCard/EntriesCard";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import DietDiary from "../DietDiary/DietDiary";
import CustomModal, {
  ModalSize,
} from "../../ui-library/CustomModal/CustomModal";
import DaySummary from "../../library/DaySummary/DaySummary";
import {
  dailyTargetIds,
  DateFormat,
  getExtendedDateFormat,
  getMacronutrientColor,
  kcalMultiplier,
  UNIT,
} from "../../../modules/constants";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";
import moment from "moment";
import {
  dailyEntries,
  mapDispatchToProps,
  mapStateToProps,
} from "./DailyEntries.store";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { useTranslation } from "react-i18next";
import {
  decimalPlaces,
  getMealEntriesString,
  roundMealPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { DEFAULT_MOMENT_LOCALE, isRtl } from "../../../i18n/helpers";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";

const DailyEntriesWrapper = styled.div`
  @media (min-width: 1279px) {
    border: 1px solid ${(props) => props.theme?.lightGray};
    margin-top: 1rem;
  }
`;

const EmptyDataBoard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelsWrapper = styled.div`
  margin-left: 0px;
  margin-right: 0px;
  width: 100%;

  & > div {
    margin-right: 15px;
    margin-bottom: 15px;
    flex: 0.45;
  }
  & > #entries-list {
    min-width: 50%;
    flex: 1;
    margin-left: 20px;
    padding: 0;
    > div {
      box-shadow: none;
      height: 100%;
    }
  }

  @media (max-width: 1279px) {
    flex-direction: column;
    & > #entries-list {
      min-width: unset;
      width: inherit;
      margin-left: 0;
    }
    & > div {
      margin-right: 0;
    }
  }
`;

const HorizontalProgressBarChartWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const DailyTargetsWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const dataStatsArrEmpty = [
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.CALORIES_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.PROTEIN_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.CARBS_ATTR_ID,
  },
];

export const DailyEntries = (props) => {
  const {
    date,
    goalsChartTitle,
    listTitle,
    listActionTitle,
    closeDietDiaryAction,
    fetchEntries,
    completeDay,
    loadingEntries,
    loadingEntriesError,
    dailyEntry,
    canCompleteDay,
    canViewMacronutrients,
    canEditMeal,
    refetchData,
    dailyGoals,
    theme,
    canDeleteMeal,
    deleteEntryMeal,
    participantDateOfEnrollment,
    title,
    dailyItemKey,
    timeFormat = "12h",
  } = props;

  const { t } = useTranslation();

  const [deletingMeal, setDeletingMeal] = useState(false);

  const dailyTargetsArr = useMemo(() => {
    return {
      isRtl: isRtl(),
      goalTitle: t("appText.dietDiary.targets.target"),
      calories: {
        calories: `${roundMealValue(dailyGoals?.calories ?? 0).toFixed(
          decimalPlaces
        )}kcal`,
        title: t("appText.dietDiary.calories"),
      },
      goalsList: [
        {
          goalLabel: t("appText.dietDiary.longChainFats"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(
            dailyGoals?.longChainFatsPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
        {
          goalLabel: t("appText.dietDiary.mediumChainFats"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(
            dailyGoals?.mediumChainFatsPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
        {
          goalLabel: t("appText.dietDiary.protein"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.PROTEIN_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(dailyGoals?.proteinPercentage ?? 0)}${
            UNIT.PERCENTAGE
          }`,
        },
        {
          goalLabel: t("appText.dietDiary.carbohydrates"),
          goalColor: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
          goalValue: `${roundMealValue(
            dailyGoals?.carbohydratesPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
      ],
    };
  }, [dailyGoals, t, theme]);

  const minDateMeal = useMemo(() => {
    if (
      participantDateOfEnrollment &&
      moment(participantDateOfEnrollment).startOf("day") <
        moment().startOf("day").subtract(1, "days")
    ) {
      return moment().startOf("day").subtract(1, "days").toDate();
    }

    return moment(participantDateOfEnrollment).startOf("day").toDate();
  }, [participantDateOfEnrollment]);

  const isAbleToCompleteDay = useMemo(() => {
    return dailyEntry?.completed || minDateMeal > date.startOf("day").toDate();
  }, [dailyEntry, minDateMeal, date]);

  const [completeDayData, setCompleteDayData] = useState(null);

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const [entryToDelete, setEntryToDelete] = useState(null);

  const [dataStats, setDataStats] = useState(dataStatsArrEmpty);
  const [dailyTotalsData, setDailyTotalsData] = useState({
    title: goalsChartTitle,
    headerData: [
      {
        label: t("appText.dietDiary.calories"),
        value: `0.0kcal`,
        color: props.theme?.magentaShade,
      },
    ],
    data: [
      {
        label: t("appText.dietDiary.longChainFats"),
        value: `0`,
        color: getMacronutrientColor(
          dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
          theme
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.mediumChainFats"),
        value: `0`,
        color: getMacronutrientColor(
          dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
          theme
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.protein"),
        value: `0`,
        color: getMacronutrientColor(dailyTargetIds.PROTEIN_ATTR_ID, theme),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.carbohydrates"),
        value: `0`,
        color: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
        unit: "%",
      },
    ],
  });

  useEffect(() => {
    if (dailyEntry?.formattedMeals) {
      const totalCaloriesCount = dailyEntry.formattedMeals.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return total + (jsonData?.aboutMeal?.Calories?.value || 0);
        },
        0
      );

      const totalCarbsCount = dailyEntry.formattedMeals.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return total + (jsonData?.aboutMeal?.Carbs?.value || 0);
        },
        0
      );

      const totalProteinCount = dailyEntry.formattedMeals.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return total + (jsonData?.aboutMeal?.Protein?.value || 0);
        },
        0
      );

      const totalLongChainFatsCount = dailyEntry.formattedMeals.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return total + (jsonData?.aboutMeal?.LongChainFats?.value || 0);
        },
        0
      );

      const totalMediumChainFatsCount = dailyEntry.formattedMeals.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return total + (jsonData?.aboutMeal?.MediumChainFats?.value || 0);
        },
        0
      );

      let carbsFreq = 0;
      let protFreq = 0;
      let longChainfatsFreq = 0;
      let mediumChainfatsFreq = 0;

      // to avoid division by 0
      if (totalCaloriesCount > 0) {
        carbsFreq =
          ((totalCarbsCount * kcalMultiplier.carbs) / totalCaloriesCount) * 100;
        protFreq =
          ((totalProteinCount * kcalMultiplier.protein) / totalCaloriesCount) *
          100;
        longChainfatsFreq =
          ((totalLongChainFatsCount * kcalMultiplier.lcf) /
            totalCaloriesCount) *
          100;
        mediumChainfatsFreq =
          ((totalMediumChainFatsCount * kcalMultiplier.mcf) /
            totalCaloriesCount) *
          100;
      }

      const dataStatsArr = [
        {
          frequency: 0,
          count: totalCaloriesCount,
          id: dailyTargetIds.CALORIES_ATTR_ID,
        },
        {
          frequency: longChainfatsFreq,
          count: totalLongChainFatsCount,
          id: dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
        },
        {
          frequency: mediumChainfatsFreq,
          count: totalMediumChainFatsCount,
          id: dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
        },
        {
          frequency: protFreq,
          count: totalProteinCount,
          id: dailyTargetIds.PROTEIN_ATTR_ID,
        },
        {
          frequency: carbsFreq,
          count: totalCarbsCount,
          id: dailyTargetIds.CARBS_ATTR_ID,
        },
      ];

      const dailyTotalsChartArr = {
        title: goalsChartTitle,
        headerData: [
          {
            label: t("appText.dietDiary.calories"),
            value: `${roundMealValue(totalCaloriesCount).toFixed(
              decimalPlaces
            )}kcal`,
            color: theme?.magentaShade,
          },
        ],
        data: [
          {
            label: t("appText.dietDiary.longChainFats"),
            value: `${roundMealPercentage(roundMealValue(longChainfatsFreq))}`,
            valueGrams: roundMealValue(totalLongChainFatsCount),
            color: getMacronutrientColor(
              dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
              theme
            ),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.mediumChainFats"),
            value: `${roundMealPercentage(
              roundMealValue(mediumChainfatsFreq)
            )}`,
            valueGrams: roundMealValue(totalMediumChainFatsCount),
            color: getMacronutrientColor(
              dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
              theme
            ),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.protein"),
            value: `${roundMealPercentage(roundMealValue(protFreq))}`,
            valueGrams: roundMealValue(totalProteinCount),
            color: getMacronutrientColor(dailyTargetIds.PROTEIN_ATTR_ID, theme),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.carbohydrates"),
            value: `${roundMealPercentage(roundMealValue(carbsFreq))}`,
            valueGrams: roundMealValue(totalCarbsCount),
            color: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
            unit: "%",
          },
        ],
      };

      setDataStats(dataStatsArr);
      setDailyTotalsData(dailyTotalsChartArr);
    }
  }, [dailyEntry, date, goalsChartTitle, theme, t]);

  const finishDayConfirm = () => {
    setCompleteDayData({
      day: date,
      dayElements: dataStats,
    });
  };

  const finishDay = () => {
    setCompleteDayData(null);
    completeDay({
      day: moment(date)
        .locale(DEFAULT_MOMENT_LOCALE)
        .format(DateFormat.INVERTED_DATE),
    })
      .then((message) => {
        refetchData(date);
        sendNotification(message, ToastType.SUCCESS);
      })
      .catch((e) => sendNotification(e, ToastType.ERROR));
  };

  const entryEdit = (element) => {
    const type = JSON.parse(element.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.edit")} ${dietDairyTypeToString(type)}`
    );

    setSelectedDietDiaryOption({
      mealId: element.mealId,
      jsonData: element.json,
      date: element.date,
      type: type,
      readOnly: element.readOnly,
      reason: element.reason,
      reasonForChange: element.reasonForChange,
      administeredBy: element.administration,
      feedingType: element.feedingType,
      endDate: element.endDate,
    });
  };

  const onDeleteEntryMeal = (entry) => {
    setDeletingMeal(true);

    deleteEntryMeal(entry.mealId)
      .then((success) => {
        setEntryToDelete(null);

        sendNotification(success, ToastType.SUCCESS);

        refetchData(date);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      })
      .finally(() => {
        setDeletingMeal(false);
      });
  };

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
    if (closeDietDiaryAction) {
      closeDietDiaryAction();
    } else {
      fetchEntries(date);
    }
  };

  const onCloseCompleteDayModal = () => {
    setCompleteDayData(null);
  };

  return (
    <DailyEntriesWrapper data-testid="daily-entries">
      <DailySectionTitle
        label={title}
        date={date}
        dailyEntry={dailyEntry}
        dailyItemKey={dailyItemKey}
        isRtl={isRtl()}
        refreshData={() => refetchData(date)}
      />
      <PanelsWrapper className="row">
        <EntriesCard
          id={dailyItemKey}
          title={listTitle || t("appText.dietDiary.entriesPanel.today.title")}
          elements={dailyEntry?.formattedMeals || []}
          completeDay={dailyEntry?.completed}
          showActionButton={canCompleteDay}
          actionButtonText={
            listActionTitle || t("appText.dietDiary.completeDay")
          }
          actionButton={finishDayConfirm}
          isFastingDay={dailyEntry?.fastingDay}
          actionButtonInactive={isAbleToCompleteDay}
          loadingEntries={loadingEntries}
          loadingEntriesError={loadingEntriesError}
          onEdit={(elementEdit) => entryEdit(elementEdit)}
          canEdit={canEditMeal && !dailyEntry?.completed}
          onDelete={(entry) => setEntryToDelete(entry)}
          canDelete={canDeleteMeal && !dailyEntry?.completed}
          timeFormat={timeFormat}
        />

        {canViewMacronutrients && (
          <HorizontalProgressBarChartWrapper>
            {dailyTotalsData ? (
              <HorizontalProgressBarChart {...dailyTotalsData} />
            ) : (
              <EmptyDataBoard>
                <p>{t("appText.noData")}</p>
              </EmptyDataBoard>
            )}
          </HorizontalProgressBarChartWrapper>
        )}

        <DailyTargetsWrapper>
          {dailyTargetsArr ? (
            <DailyTargets {...dailyTargetsArr} />
          ) : (
            <LoadingSpinner />
          )}
        </DailyTargetsWrapper>
      </PanelsWrapper>

      <CustomModal
        title={t("appText.confirmRequest.title")}
        onClose={() => setEntryToDelete(null)}
        show={!!entryToDelete}
      >
        <ConfirmRequest
          message={`${t("appText.dietDiary.removeEntries.message", {
            date: `<br/><b>${moment(entryToDelete?.date).format(
              getExtendedDateFormat()
            )}</b>`,
          })}<br/><br/>${getMealEntriesString(entryToDelete)}.`}
          warningMessage={t("appText.dietDiary.removeEntries.confirm")}
          onConfirm={() => onDeleteEntryMeal(entryToDelete)}
          onClose={() => setEntryToDelete(null)}
          submitting={deletingMeal}
        />
      </CustomModal>

      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size={ModalSize.large}
        onClose={() => onCloseDietDiaryModal()}
      >
        <DietDiary
          mealJsonData={selectedDietDiaryOption?.jsonData}
          mealId={selectedDietDiaryOption?.mealId}
          onSubmit={() => onCloseDietDiaryModal()}
          onClose={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          mealDate={selectedDietDiaryOption?.date}
          readOnly={selectedDietDiaryOption?.readOnly}
          dietDiaryOption={selectedDietDiaryOption}
          setModalTitle={setDietDiaryModalTitle}
          existingReasonForChange={selectedDietDiaryOption?.reasonForChange}
          type={selectedDietDiaryOption?.type}
          mealAdministeredBy={selectedDietDiaryOption?.administeredBy}
          mealFeedingType={selectedDietDiaryOption?.feedingType}
          mealEndDate={selectedDietDiaryOption?.endDate}
        />
      </CustomModal>

      {completeDayData !== null && dataStats !== null && (
        <CustomModal
          show={completeDayData !== null && dataStats !== null}
          title={t("appText.dietDiary.completeDay.title")}
          size={ModalSize.small}
          onClose={() => onCloseCompleteDayModal()}
        >
          <DaySummary
            isFastingDayConfirmation={dataStats.every(
              (item) => item.count === 0
            )}
            isModal={true}
            completeDayData={completeDayData}
            dataStats={dataStats}
            finishDay={() => finishDay()}
            closeModal={() => onCloseCompleteDayModal()}
          />
        </CustomModal>
      )}
    </DailyEntriesWrapper>
  );
};

const combineMapStateToProps = (state) => ({
  ...state,
  ...mapStateToProps(state),
});

export default withTheme(
  dynamic(
    "dailyEntries",
    subspaced({
      // We map extra state because we might need to acess other stores for data, subspace is self contained.
      mapExtraState: (state, rootState) => ({
        loginReducer: { ...state.loginReducer },
        smartPageHeaderReducer: { ...state.smartPageHeaderReducer },
        timeFormat: state.smartPageHeaderReducer?.participantTimeFormat,
      }),
    }),
    attachReducer(dailyEntries)
  )(connect(combineMapStateToProps, mapDispatchToProps)(DailyEntries))
);
